import { AppShellLoginState } from '@westwing/appshell_types';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import { useEffect } from 'react';
import useIsKnownCustomer from './useIsKnownCustomer';
import { useShopToClubOverlayConfig } from './useShopToClubOverlayConfig';
import useWasOverlayShown from './useWasOverlayShown';
import { oneDayTTL } from './useBellaCookies';
import { OverlayShownLabel } from 'Interfaces/overlayTracking';
import { OverlayOpenOptions } from 'Types/types';
import { useHostProps } from 'Hooks/useHostProps';
import { cookiesEnabled } from 'Helpers/environment';
import { useAppshellMeta, useFeatureToggle, useLoginState } from 'AppShell/hooks';
import { useGaTracking } from 'AppShell/TrackingHelper';
import isClubMfPath from 'SharedHelpers/isClubMfPath';

export enum PAGE_VIEW {
    firstPageView = 'first_page_view',
    secondPageView = 'second_page_view',
}

export const SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION = 'ww_shop_to_club_overlay_shown_in_this_session';

export const useShouldShowShopToClub = (
    handleOverlayOpen: (openOptions: OverlayOpenOptions) => void,
    isOpen: boolean
): boolean => {
    const [hasBeenViewed, setHasBeenViewedStatus] = useWasOverlayShown();
    const loginState = useLoginState();
    const { isLoggedIn, didFinishOnce, isRegistered, isReturningUser } = loginState as AppShellLoginState;
    const { isApp } = useAppshellMeta();
    const [isKnownUser] = useIsKnownCustomer();
    const { p100DisableS2CFor2ndPageView } = useFeatureToggle();
    const { trackVariation } = useGaTracking();
    const isClubMf = isClubMfPath();

    const isActive = isOpen;

    const { pageViewLimit, expirationInDays, mobileDelayInSeconds } = useShopToClubOverlayConfig();

    const { isShopToClubEnabled } = useHostProps();
    const isDisabledFromBob = !isShopToClubEnabled;

    let canShowOnThisPage = isReturningUser && !isActive && !hasBeenViewed;
    if (isDesktop() && pageViewLimit === PAGE_VIEW.firstPageView) {
        canShowOnThisPage = !isActive && !hasBeenViewed;
    }

    const failConditions = [
        isClubMf,
        isRegistered,
        !didFinishOnce,
        isApp,
        isLoggedIn,
        false, // TODO Validate config here from useShopToClubOverlayConfig()
        isDisabledFromBob,
        !canShowOnThisPage,
        isKnownUser,
    ];

    const skipRender = failConditions.find(condition => Boolean(condition));

    useEffect(() => {
        const cookieTTL = expirationInDays * oneDayTTL;
        const wasExpirationDisabled = !expirationInDays && hasBeenViewed;
        if (wasExpirationDisabled && cookiesEnabled) {
            setHasBeenViewedStatus(0, cookieTTL);
        }

        if (!skipRender && cookiesEnabled) {
            trackVariation('p100DisableS2CFor2ndPageView', p100DisableS2CFor2ndPageView);
            if (!p100DisableS2CFor2ndPageView) {
                const mobileDelayMS = mobileDelayInSeconds * 1000;
                const overlayTimer = setTimeout(() => {
                    handleOverlayOpen({
                        isLogin: false,
                        isDismissible: true,
                        isUndismissibleFromOutsideClick: false,
                        trackingLabel: OverlayShownLabel.SecondPageLoad,
                    });
                }, mobileDelayMS);

                return () => clearTimeout(overlayTimer);
            }
        }
        return () => ({});
    }, [
        canShowOnThisPage,
        expirationInDays,
        handleOverlayOpen,
        hasBeenViewed,
        mobileDelayInSeconds,
        setHasBeenViewedStatus,
        skipRender,
        p100DisableS2CFor2ndPageView,
        trackVariation,
    ]);

    return !skipRender && !p100DisableS2CFor2ndPageView;
};
